import { ReactNode } from 'react';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import dynamic from 'next/dynamic';

import UpcomingCounterContent from '@/common/components/UpcomingCounter/UpcomingCounterContent';
import { formatCounterToStringDate } from '@/common/hooks/useCountdown/formatter/stringDateFormatter';
import useCountdown from '@/common/hooks/useCountdown/useCountdown';
import SvgIconCalendar from '@/common/icons/generated/common/IconCalendar';

import styles from './upcomingCounter.module.scss';

dayjs.extend(relativeTime);

interface Props {
  date: string;
  onEnd?: () => void;
}

// This component must be loaded dynamically on client side, thats why its dynamic. If this would be rendered on server (SSR) hydration errors can occur.
export const ClientSideUpcomingCounter = ({ date, onEnd }: Props) => {
  const now = dayjs();
  const counterTime = dayjs(date);
  const mode = now.add(1, 'day').isBefore(counterTime) ? 'relative' : 'counter';

  const timeLeft = useCountdown(counterTime, { enabled: mode === 'counter', onEnd });
  const formattedTimeLeft = formatCounterToStringDate(timeLeft);

  const content: Record<typeof mode, ReactNode> = {
    relative: now.to(counterTime, true),
    counter: <UpcomingCounterContent timeLeft={formattedTimeLeft} />,
  };

  return (
    <span className={styles.counter} data-testid="upcoming-counter">
      <SvgIconCalendar className={styles.icon} />
      In {content[mode]}
    </span>
  );
};

export const UpcomingCounter = dynamic(
  () =>
    import('@/common/components/UpcomingCounter/UpcomingCounter').then(
      (module) => module.ClientSideUpcomingCounter,
    ),
  { ssr: false },
);

export default UpcomingCounter;
